/**
 * Card 325 — reading guest responses, as pure functions.
 *
 * Each response carries a snapshot of the labels it was answered against
 * (TA-Ticketing questionnaire model, buildSnapshot), in English. Everything
 * shown to head office is read from that snapshot rather than the live
 * questionnaire, so rewording a question never changes what an old answer
 * appears to say.
 */
import { formatDayFirst } from '@utils/dayFirstDates';

export const RATING_WORDS = ['', 'Very poor', 'Poor', 'Okay', 'Good', 'Very good'];

export const RATING_FILTERS = [
  { value: '', label: 'All' },
  { value: 'check', label: 'Needs attention' },
  { value: 'negative', label: 'Unhappy' },
  { value: 'neutral', label: 'In between' },
  { value: 'positive', label: 'Delighted' }
];

export const emptyFilters = () => ({
  tourId: '',
  departureDate: '',
  guideId: '',
  from: '',
  to: '',
  rating: '',
  // Insights drill-down: the exact responses behind a count, comma-separated.
  // Not a visible control; the Responses tab says what it is showing instead.
  ids: ''
});

/** Query params for the API: blanks dropped, departure only with its tour. */
export const toQuery = (filters, extra = {}) => {
  const query = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value) query[key] = value;
  });
  if (!query.tourId) delete query.departureDate;
  return { ...query, ...extra };
};

export const ratingWord = score => RATING_WORDS[score] || '';

/** An answer, as head office reads it, from the response's own snapshot. */
export const formatAnswer = (question, value) => {
  if (value === undefined || value === null) return '';
  switch (question.type) {
    case 'rating5':
      return `${value} · ${ratingWord(value)}`;
    case 'yesno':
      return value ? 'Yes' : 'No';
    case 'single': {
      const option = (question.options || []).find(o => o.id === value);
      return option ? option.label : String(value);
    }
    case 'multi':
      return (Array.isArray(value) ? value : [value])
        .map(id => ((question.options || []).find(o => o.id === id) || {}).label || id)
        .join(', ');
    default:
      return String(value);
  }
};

/**
 * The answered questions in the order the guest met them, with the long-text
 * and contact questions left out (they have their own places in the drawer).
 */
export const answeredQuestions = response => {
  const answers = new Map((response.answers || []).map(a => [a.questionId, a.value]));
  return (response.snapshot?.questions || [])
    .filter(q => !['longText', 'name', 'email'].includes(q.type) && answers.has(q.id))
    .map(q => ({
      id: q.id,
      section: q.section,
      label: q.label,
      text: formatAnswer(q, answers.get(q.id))
    }));
};

/** Hotels and experiences the guest rated or marked as not used, grouped. */
export const itineraryRatings = response => {
  const given = new Map((response.itinerary || []).map(i => [i.itemId, i.value]));
  const rows = (response.snapshot?.itinerary || [])
    .filter(item => given.has(item.id))
    .map(item => {
      const value = given.get(item.id);
      return {
        id: item.id,
        kind: item.kind,
        name: item.name,
        detail: [item.service, item.place].filter(Boolean).join(', '),
        text: value === null ? 'Did not use' : `${value} · ${ratingWord(value)}`,
        score: value
      };
    });
  return {
    hotels: rows.filter(r => r.kind === 'hotel'),
    experiences: rows.filter(r => r.kind !== 'hotel')
  };
};

export const needsAttention = response =>
  response.rating === 'negative' ||
  Boolean(response.comment && response.llmCheck && response.llmCheck.mismatch);

export const excerpt = (text, max = 140) => {
  if (!text) return '';
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 60 ? cut.lastIndexOf(' ') : max).trim()}…`;
};

// Day-first ("20 Sept 2026"), not the browser's locale (QA 2026-10-20).
export const formatDeparture = date =>
  formatDayFirst(date, { day: 'numeric', month: 'short', year: 'numeric' });

export const formatSubmitted = value =>
  formatDayFirst(value, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export const LANGUAGE_NAMES = { en: 'English', fr: 'French' };
